/**
 * Qui parle dans la salle, lu sur l'événement « parle / se tait » de LiveKit (`ActiveSpeakersChanged`) : la seule
 * source des barres et du liseré vert des vignettes, la mienne comprise.
 *
 * Un seul écouteur pour toute la salle, posé tant qu'une vignette écoute. À chaque changement, seules les vignettes
 * des personnes qui se sont mises à parler ou se sont tues sont prévenues : la page, elle, ne l'apprend jamais.
 * Aucune lecture du niveau de la voix.
 *
 * Une arrivée, un départ ou une reconnexion relit la salle. Le SDK garde dans `activeSpeakers` un participant parti
 * en pleine phrase, et ne prévient de rien : seuls comptent ceux qui sont encore là, sous la même session (`sid`).
 */
import { ConnectionState, type Participant, RoomEvent } from "livekit-client"

import { userIdOfIdentity } from "./dataChannel"

type Speaker = Pick<Participant, "identity" | "sid" | "isLocal">

/** Ce que la source lit de la salle : ses événements, son état de connexion, qui y est et qui parle. */
export type SpeakingRoom = {
    state: ConnectionState
    activeSpeakers: readonly Speaker[]
    remoteParticipants: ReadonlyMap<string, Pick<Participant, "sid">>
    on: (event: RoomEvent, listener: (...args: never[]) => void) => unknown
    off: (event: RoomEvent, listener: (...args: never[]) => void) => unknown
}

export type SpeakingSource = {
    isSpeaking: (personId: string) => boolean
    subscribe: (personId: string, onChange: () => void) => () => void
}

/** Les personnes (l'id de leur vignette) dont un appareil encore dans la salle parle. */
function speakersOf(room: SpeakingRoom): Set<string> {
    const ids = new Set<string>()
    if (room.state !== ConnectionState.Connected) return ids
    room.activeSpeakers.forEach((speaker) => {
        const present = speaker.isLocal || room.remoteParticipants.get(speaker.identity)?.sid === speaker.sid
        const id = present ? userIdOfIdentity(speaker.identity) : null
        if (id != null) ids.add(id)
    })
    return ids
}

/** Ce qui relit la salle : qui parle a changé, quelqu'un est arrivé ou parti, la salle s'est coupée ou reconnectée. */
const RESYNC_EVENTS = [
    RoomEvent.ActiveSpeakersChanged,
    RoomEvent.ParticipantConnected,
    RoomEvent.ParticipantDisconnected,
    RoomEvent.ConnectionStateChanged,
] as const

export function speakingSource(room: SpeakingRoom): SpeakingSource {
    let speaking = new Set<string>()
    const listeners = new Map<string, Set<() => void>>()

    const update = (next: Set<string>) => {
        // Un id qui n'est que dans l'un des deux ensembles : il y apparaît une seule fois.
        const changed = [...speaking, ...next].filter((id) => speaking.has(id) !== next.has(id))
        speaking = next
        changed.forEach((id) => listeners.get(id)?.forEach((notify) => notify()))
    }
    // Salle coupée : personne ne parle (le SDK ne remet pas « parle » à faux à la coupure) ; reconnectée, elle se relit.
    const resync = () => update(speakersOf(room))
    const listening = () => {
        speaking = speakersOf(room)
        RESYNC_EVENTS.forEach((event) => room.on(event, resync))
    }
    const stop = () => {
        RESYNC_EVENTS.forEach((event) => room.off(event, resync))
        speaking = new Set()
    }

    return {
        isSpeaking: (personId) => speaking.has(personId),
        subscribe: (personId, onChange) => {
            if (listeners.size === 0) listening()
            const own = listeners.get(personId) ?? new Set()
            own.add(onChange)
            listeners.set(personId, own)
            return () => {
                own.delete(onChange)
                if (own.size === 0) listeners.delete(personId)
                if (listeners.size === 0) stop()
            }
        },
    }
}
